import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';
import CSSModules from 'react-css-modules';
import isNull from 'lodash/isNull';
import camelize from 'camelize';
import cn from 'classnames';
import get from 'lodash/get';

import Icon from '../Icon';
import Button from '../Button';
import TextInput from '../TextInput';
import Loader from '../common/Loader';
import Checkbox from '../common/Checkbox';
import ErrorBlock from '../common/ErrorBlock';
import PasswordInput from '../TextInput/PasswordInput';
import { AUTH, PROFILE } from '../../constants/strings';
import handleApiError from '../../utils/handleApiError';
import { MESSAGES, LOGIN, SIGN_UP_AZURE } from '../../constants/routes';
import { invitationMethods } from '../../redux/auth/status';
import { getPhoneWithoutUSCode } from '../../utils/getPhone';
import validateForm from '../../utils/auth/editUserValidation';
import {
  isLoggedInSelector,
  selectSuccessAuthAzureForSignUpFlow,
} from '../../redux/auth/selectors';
import { signupActions } from '../../redux/auth/signup/actions';
import { selectEditedUser } from '../../redux/profiles/selectors';
import { loggedUserSelector } from '../../redux/context/selectors';
import { actions as authAzureActions } from '../../redux/auth/authAzure';
import { actions as countryDetailsActions } from '../../redux/data/countryDetails';
import {
  privacyListSelector,
  selectCountryList,
} from '../../redux/data/selectors';
import {
  selectAuthStatusInvitationType,
  selectAuthStatusInvitationMethod,
  selectSignupProcessFormData,
  selectUsernameAvailabilityError,
} from '../../redux/auth/signup/selectors';
import styles from './index.styles';
import MetaHelmet from '../AppHelmet/MetaHelmet';
import { GATrackPageView } from '../../gtag';
import CheckingListTooltip from '../CheckPasswordList';
import {
  getUsernameTooltipOptions,
  validateUsernameCharacters,
  validateUsernameLength,
  validateUsernameRepeating,
  validateUsernameSpaces,
} from './utils/usernameValidation';

const mapStateToProps = state => ({
  user: loggedUserSelector(state),
  invitationType: selectAuthStatusInvitationType,
  loggedIn: isLoggedInSelector(state),
  editUser: selectEditedUser(state),
  usernameAvailabilityError: selectUsernameAvailabilityError(state),
  invitationMethod: selectAuthStatusInvitationMethod(state),
  countries: selectCountryList(state),
  privacyList: privacyListSelector(state),
  signupFormData: selectSignupProcessFormData(state),
  isSuccessAuthAzureForSignUp: selectSuccessAuthAzureForSignUpFlow(state),
});

const mapDispatchToProps = {
  proceed: signupActions.proceed,
  authAzure: authAzureActions.init,
  getDetailsCountry: countryDetailsActions.send,
};

@injectIntl
@connect(
  mapStateToProps,
  mapDispatchToProps,
)
@CSSModules(styles, { allowMultiple: true })
export default class SignUp extends PureComponent {
  state = {
    errors: null,
    submitted: false,
    password: null,
    passwordConfirmation: null,
    isValidPassword: false,
    phoneNumberConfirmed: true,
    emailConfirmed: true,
    isOpenPopover: false,
  };

  componentDidMount() {
    const { signupFormData, getDetailsCountry } = this.props;
    getDetailsCountry({ code: 'us' });

    GATrackPageView('Sign Up');
    if (signupFormData) {
      const { passwordConfirmation, password, username } = camelize(
        signupFormData,
      );

      this.setState({
        password,
        passwordConfirmation,
        username,
        isValidPassword: true,
      });
    }
  }

  componentWillReceiveProps(nextProps) {
    // eslint-disable-next-line react/destructuring-assignment
    if (this.props.serializedForm !== nextProps.serializedForm) {
      const form = nextProps.serializedForm;
      const errors = validateForm(form);

      this.setState({
        errors,
        submitted: true,
      });
    }
  }

  getEmailField(value, classes) {
    const {
      intl: { formatMessage },
    } = this.props;

    return (
      <TextInput
        key={1}
        className={classes}
        disabled
        id="signup-email"
        label={formatMessage(AUTH.fields.email)}
        name="email"
        value={value}
        placeholder={formatMessage(AUTH.fields.email)}
        rounded
        type="email"
      />
    );
  }

  getPhoneNumberField(value, classes) {
    const {
      intl: { formatMessage },
    } = this.props;

    return (
      <TextInput
        key={2}
        className={classes}
        disabled
        id="signup-phoneNumber"
        label={formatMessage(AUTH.fields.phoneNumber)}
        name="phoneNumber"
        value={getPhoneWithoutUSCode(value)}
        placeholder={formatMessage(AUTH.fields.phoneNumber)}
        rounded
        type="text"
      />
    );
  }

  get isNotFakeEmail() {
    const {
      user: { email, emailFake },
    } = this.props;
    return email && !emailFake;
  }

  getSubtitleId = () => {
    const { isNotFakeEmail, props } = this;
    const {
      user: { phoneNumber },
    } = props;
    let id = 'auth.signup.text.phone';
    if (isNotFakeEmail) id = 'auth.signup.text.email';
    if (isNotFakeEmail && phoneNumber) id = 'auth.signup.text.emailPhone';
    return id;
  };

  handleConfirm = (type, value) => {
    this.setState({
      [type]: value,
    });
  };

  handlePassword = (type, value, invalid) => {
    this.setState({
      [type]: value,
      isValidPassword: !invalid,
    });
  };

  handleTypeUsername = username => {
    this.setState({ username });
  };

  handleOpenPopover = () => {
    this.setState({ isOpenPopover: true });
  };

  handleClosePopover = () => {
    this.setState({ isOpenPopover: false });
  };

  render() {
    const { isNotFakeEmail } = this;
    const {
      errors,
      submitted,
      password,
      passwordConfirmation,
      isValidPassword,
      phoneNumberConfirmed,
      emailConfirmed,
      username,
      isOpenPopover,
    } = this.state;

    const {
      usernameAvailabilityError,
      editUser: { error, rejected },
      getAction,
      user,
      proceed,
      invitationType,
      loggedIn,
      intl: { formatMessage },
      invitationMethod,
      countries,
      privacyList,
      authAzure,
      isSuccessAuthAzureForSignUp,
    } = this.props;

    const isValidUsername =
      validateUsernameLength(username) &&
      validateUsernameCharacters(username) &&
      validateUsernameSpaces(username) &&
      validateUsernameRepeating(username);

    if (!invitationType) {
      const route = loggedIn ? MESSAGES : LOGIN;
      browserHistory.push(route);
    }

    if (isSuccessAuthAzureForSignUp) {
      browserHistory.push(SIGN_UP_AZURE);
    }

    const isPasswordsChecked = password === passwordConfirmation;
    // allow api request if front validation passed
    if (submitted && !errors && isPasswordsChecked) {
      getAction(data => {
        proceed(data);
      });
    }

    const { email, phoneNumber } = user;

    const passwordConfirmationError =
      !isPasswordsChecked && formatMessage(AUTH.actions.signup.matchPassword);

    const availabilityError =
      get(usernameAvailabilityError, 'description', null) &&
      formatMessage(PROFILE.errors.username.taken);

    const errorEncountered =
      rejected ||
      (submitted && !!errors) ||
      !!passwordConfirmationError ||
      !!availabilityError;
    // eslint-disable-next-line no-nested-ternary
    const validationErrors = rejected
      ? handleApiError(error) || formatMessage(AUTH.errors.common)
      : submitted
      ? errors
      : null;
    const hasPassword = !isNull(password) && !isNull(passwordConfirmation);
    const isEnabled =
      isPasswordsChecked && hasPassword && isValidPassword && isValidUsername;
    const isLoadedPrivacyAndCountries = privacyList.length && countries.length;

    const inputFieldCn = cn(styles.labelField, styles.inputField, 'mb3');

    const handleProceedAzureSignUp = e => {
      e.preventDefault();
      authAzure({ intl: formatMessage, signUpFlow: true });
    };

    return (
      <Fragment>
        <MetaHelmet title="pages.title.signup" />

        {user && invitationType && isLoadedPrivacyAndCountries ? (
          <div className={styles.formWrapper}>
            <p className={styles.formTitle}>
              <FormattedMessage {...AUTH.titles.signup} />
              <span className={styles.formStep}> (1/2)</span>
            </p>
            <p className={styles.formText}>
              {formatMessage({ id: this.getSubtitleId() })}
            </p>

            {invitationMethod === invitationMethods.phoneNumber && (
              <Fragment>
                {this.getPhoneNumberField(phoneNumber, inputFieldCn)}
                {isNotFakeEmail && (
                  <Fragment>
                    {this.getEmailField(email, inputFieldCn)}
                    <Checkbox
                      className={styles.checkboxConfirm}
                      id="emailConfirmed"
                      name="emailConfirmed"
                      value={emailConfirmed}
                      checked={emailConfirmed}
                      label={formatMessage(AUTH.titles.confirmMyEmail)}
                      onChange={value =>
                        this.handleConfirm('emailConfirmed', value)
                      }
                    />
                  </Fragment>
                )}
              </Fragment>
            )}
            {invitationMethod === invitationMethods.email && (
              <Fragment>
                {this.getEmailField(email, inputFieldCn)}
                {phoneNumber && (
                  <Fragment>
                    {this.getPhoneNumberField(phoneNumber, inputFieldCn)}
                    <Checkbox
                      className={styles.checkboxConfirm}
                      id="phoneNumberConfirmed"
                      name="phoneNumberConfirmed"
                      checked={phoneNumberConfirmed}
                      value={phoneNumberConfirmed}
                      label={formatMessage(AUTH.titles.confirmMyPhoneNumber)}
                      onChange={value =>
                        this.handleConfirm('phoneNumberConfirmed', value)
                      }
                    />
                  </Fragment>
                )}
              </Fragment>
            )}
            <PasswordInput
              autoComplete="new-password"
              className={inputFieldCn}
              withValidation
              id="password"
              name="password"
              rounded
              value={password}
              onChange={this.handlePassword}
            />
            <PasswordInput
              autoComplete="new-password"
              className={inputFieldCn}
              id="passwordConfirmation"
              name="passwordConfirmation"
              value={passwordConfirmation}
              rounded
              placeholder={formatMessage(
                AUTH.actions.signup.confirmationPassword,
              )}
              onChange={this.handlePassword}
              withValidation
            />
            <TextInput
              className="mb3 relative"
              id="signup-username"
              label={
                <div className="df items-end">
                  <FormattedMessage {...AUTH.fields.username} />
                </div>
              }
              name="username"
              value={username}
              placeholder={formatMessage({ ...AUTH.fields.username })}
              rounded
              type="text"
              onChange={this.handleTypeUsername}
              onFocus={this.handleOpenPopover}
              onBlur={this.handleClosePopover}
            >
              {isOpenPopover && (
                <CheckingListTooltip
                  title="auth.signup.popover.title"
                  list={getUsernameTooltipOptions(username)}
                >
                  <div className={styles.popoverInfo}>
                    <Icon name="icon-info" size={16} />
                    {formatMessage({ id: 'auth.signup.text.username' })}
                  </div>
                </CheckingListTooltip>
              )}
            </TextInput>
            {errorEncountered && (
              <ErrorBlock
                className={styles.errorBlock}
                errors={
                  availabilityError ||
                  passwordConfirmationError ||
                  validationErrors
                }
              />
            )}

            <Button
              className={cn(styles.acceptBtn, 'db mb3 w-100 lh-copy')}
              color="primary"
              raised
              type="submit"
              disabled={!isEnabled}
            >
              <FormattedMessage id="auth.nextStep" />
            </Button>
            <div className={styles.loginAzureWrapper}>
              <div className={styles.dividerOr}>
                {formatMessage({ id: 'auth.otherLoginOptions' })}
              </div>
              <Button
                className={cn(
                  styles.acceptBtn,
                  styles.microsoftButton,
                  'db mb3 w-100 lh-copy',
                )}
                color="accent"
                raised
                onClick={handleProceedAzureSignUp}
              >
                <Icon
                  name="icon-microsoft"
                  size={24}
                  className={styles.microsoftLogo}
                />
                <FormattedMessage {...AUTH.submit.signUpAzure} />
              </Button>
            </div>
          </div>
        ) : (
          <Loader />
        )}
      </Fragment>
    );
  }
}

SignUp.propTypes = {
  editUser: PropTypes.shape({
    error: PropTypes.object,
    rejected: PropTypes.bool,
    succeed: PropTypes.bool,
  }),
  getAction: PropTypes.func,
  user: PropTypes.shape({
    emailFake: PropTypes.bool,
    id: PropTypes.number,
    email: PropTypes.string,
    bio: PropTypes.string,
    username: PropTypes.string,
    healthInterests: PropTypes.arrayOf(PropTypes.string),
    displayableName: PropTypes.string,
    avatar: PropTypes.objectOf(PropTypes.string),
    phoneNumber: PropTypes.string,
    primaryProviderProfile: PropTypes.object,
    primaryPatientProfile: PropTypes.object,
    country: PropTypes.string,
    userOrganizations: PropTypes.array,
  }),
  redirectTo: PropTypes.string,
  proceed: PropTypes.func,
  serializedForm: PropTypes.object,
  invitationType: PropTypes.string,
  loggedIn: PropTypes.bool,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  invitationMethod: PropTypes.string,
  countries: PropTypes.array,
  signupFormData: PropTypes.object,
  usernameAvailabilityError: PropTypes.object,
  privacyList: PropTypes.array,
  getDetailsCountry: PropTypes.func,
  authAzure: PropTypes.func,
  isSuccessAuthAzureForSignUp: PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/components/Auth/SignUp.jsx